import React, { useRef } from "react";
import {
    Flame,
    FlaskConical,
    ArrowUpRight,
    Clock,
    CheckCircle2,
    Leaf,
    Zap,
    AlertTriangle,
    Wrench,
    TrendingUp,
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { Reveal, MaskReveal, smoothScrollTo } from "../lib/motion";

const IMG = {
    ocean: "https://images.unsplash.com/photo-1476897017502-219c9169bd6f?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80",
    refinery:
        "https://images.unsplash.com/photo-1602499211425-b2b286df70fa?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    towers: "https://images.unsplash.com/photo-1591200834528-4050ce99fe78?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    pipes: "https://images.unsplash.com/photo-1538474705339-e87de81450e8?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80",
    mangrove:
        "https://images.unsplash.com/photo-1556768555-ee22fbdc8a61?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80",
};

const Chapter = ({ label, dark = false }) => (
    <span
        className="section-label"
        style={dark ? { color: "rgba(255,255,255,0.6)" } : undefined}
    >
        {label}
    </span>
);

// ---------------- 01 · OPENING STATEMENT ----------------
const problems = [
    {
        id: "energy-cost",
        icon: Zap,
        title: "High energy cost",
        body: "SWRO consumes 3.5–4 Wh/L, requiring 50–80 bar of constant pressure: one of the most energy-intensive industrial processes.",
    },
    {
        id: "hazardous-waste",
        icon: AlertTriangle,
        title: "Hazardous waste",
        body: "Brine ends up damaging marine ecosystems. Polysulfone membranes degrade over time, creating hazardous waste.",
    },
    {
        id: "skilled-dep",
        icon: Wrench,
        title: "Skilled dependency",
        body: "Extensive pre/post-treatment and skilled manpower make SWRO unaffordable to deploy in water-stressed regions.",
    },
    {
        id: "stale-market",
        icon: TrendingUp,
        title: "$31B market, stale tech",
        body: "Projected to reach $31B by 2030 (CAGR ~11.4%), yet the core technology hasn't fundamentally changed in decades.",
    },
];

export const OpeningStatement = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const yImg = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

    return (
        <section
            ref={ref}
            id="opening-statement"
            data-testid="opening-statement"
            className="relative overflow-hidden bg-ocean-deep grain py-28 md:py-40 px-6 md:px-10"
        >
            <motion.div
                aria-hidden="true"
                style={{ y: yImg }}
                className="absolute inset-0 -top-[12%] -bottom-[12%] pointer-events-none bg-cover bg-center opacity-[0.18]"
            >
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url('${IMG.ocean}')` }}
                />
            </motion.div>
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "linear-gradient(180deg, #081B2E 0%, rgba(8,27,46,0.7) 40%, rgba(8,27,46,0.85) 100%)",
                }}
            />

            <div className="relative max-w-7xl mx-auto">
                {/* Why we exist */}
                <Reveal>
                    <Chapter label="Why we exist" dark />
                </Reveal>

                <h2 className="mt-10 max-w-5xl font-display font-black text-white text-3xl sm:text-4xl md:text-6xl lg:text-[4.25rem] leading-[1.03] tracking-tighter">
                    <MaskReveal
                        lines={[
                            <>Humanity decided fresh water</>,
                            <>
                                is something you{" "}
                                <span className="text-white/40">find</span>,
                            </>,
                            <>
                                not something you{" "}
                                <span className="text-energy italic">
                                    make
                                </span>
                                .
                            </>,
                        ]}
                    />
                </h2>

                <Reveal delay={0.2}>
                    <p className="mt-8 max-w-2xl text-white/70 text-base md:text-xl leading-relaxed">
                        OceanToOasis changes that, and makes it planet friendly.
                    </p>
                </Reveal>

                {/* The problem */}
                <div className="mt-24 md:mt-32 pt-16 md:pt-20 border-t border-white/10">
                    <Reveal className="max-w-3xl">
                        <span
                            className="section-label"
                            style={{ color: "rgba(255,255,255,0.6)" }}
                        >
                            The Problem
                        </span>
                        <h2 className="mt-4 font-display font-black text-white text-3xl sm:text-4xl md:text-5xl tracking-tighter leading-[1.06]">
                            Seawater Reverse Osmosis today is not
                            environmentally sustainable.
                        </h2>
                        <p className="mt-5 text-white/70 text-base md:text-lg leading-relaxed">
                            2.3 billion people face water scarcity. By 2030,
                            demand will exceed supply by 40%, and the dominant
                            fix could be making things worse.
                        </p>
                    </Reveal>
                    <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {problems.map((it, i) => (
                            <Reveal key={it.id} delay={0.1 + i * 0.08} y={30}>
                                <div
                                    className="glass-dark rounded-2xl p-7 h-full glass-card-hover"
                                    data-testid={`problem-card-${i}`}
                                >
                                    <div className="w-11 h-11 rounded-xl bg-energy/15 text-energy flex items-center justify-center">
                                        <it.icon size={20} />
                                    </div>
                                    <h3 className="mt-5 font-display font-bold text-white text-lg tracking-tight">
                                        {it.title}
                                    </h3>
                                    <p className="mt-3 text-white/60 text-sm leading-relaxed">
                                        {it.body}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

// ---------------- THESIS ----------------
export const Thesis = () => (
    <section
        data-testid="thesis-section"
        className="relative py-24 md:py-32 px-6 md:px-10 bg-lavender overflow-hidden"
    >
        <div className="max-w-7xl mx-auto">
            <Reveal>
                <Chapter label="Our thesis" />
            </Reveal>
            <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                <div>
                    <h2 className="font-display font-black text-ocean text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.04]">
                        <MaskReveal
                            lines={[
                                <>The energy for generating</>,
                                <>the world's freshwater is in</>,
                                <>
                                    <span className="text-teal">
                                        industrial waste.
                                    </span>
                                </>,
                            ]}
                        />
                    </h2>
                    <Reveal delay={0.15}>
                        <p className="mt-8 text-inkblue text-base md:text-lg leading-relaxed">
                            Every coastal refinery, steel mill, data centre and
                            power station rejects enormous quantities of
                            low-grade heat and spent chemistry: liabilities on
                            someone's balance sheet, dumped metres from the sea.
                        </p>
                    </Reveal>
                </div>

                <Reveal delay={0.1} y={60}>
                    <figure className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-[0_30px_80px_rgba(15,42,68,0.25)]">
                        <img
                            src={IMG.refinery}
                            alt="Coastal industrial facility beside the sea"
                            className="w-full h-full object-cover"
                            loading="lazy"
                        />
                        <div
                            className="absolute inset-0"
                            style={{
                                background:
                                    "linear-gradient(180deg, rgba(8,27,46,0) 40%, rgba(8,27,46,0.85) 100%)",
                            }}
                        />
                        <figcaption className="absolute bottom-0 left-0 p-6 md:p-8">
                            <span className="section-label text-energy">
                                Waste as feedstock
                            </span>
                            <p className="mt-2 text-white font-display font-bold text-lg md:text-xl tracking-tight">
                                Wasted energy. Wasted chemistry. Free ocean
                                water.
                            </p>
                        </figcaption>
                    </figure>
                </Reveal>
            </div>
        </div>
    </section>
);

// ---------------- PRODUCTS · TWO CLOCKS ----------------
const products = [
    {
        tag: "01",
        icon: Flame,
        name: "Waste-Heat Thermal Desalination",
        status: "Available today",
        trl: "TRL 7–8",
        statusIcon: CheckCircle2,
        img: IMG.towers,
        blurb: "Low-temperature thermal desalination running on heat industry already throws away: 80–90°C condenser streams, data-centre cooling loops, and CSP exhaust steam.",
        points: [
            "Turns a cooling load into a water supply",
            "Deployable now, with technology pioneered in India by our CTO",
            "Also purifies to ultra-low TDS for pharma & semiconductors",
        ],
        cta: { label: "Explore consulting", to: "/consulting" },
        accent: "energy",
    },
    {
        tag: "02",
        icon: FlaskConical,
        name: "Climate-Positive Seawater Deionizer (CPSD)",
        status: "In development",
        trl: "TRL 3–4",
        statusIcon: Clock,
        img: IMG.pipes,
        blurb: "Put ion-selective membranes between spent acid and base, seawater in the middle. The neutralization energy deionizes the seawater and generates electricity spontaneously.",
        points: [
            "Three products: fresh water, electricity, neutral effluent",
            "Ambient temperature and pressure, no high-pressure pumps",
            "Bench-scale energy: −6 to −9.7 Wh/L (generated)",
        ],
        cta: { label: "See how it works", section: "technology" },
        accent: "teal",
    },
];

const ProductCard = ({ p, i }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "0px 0px -12% 0px" });
    const StatusIcon = p.statusIcon;
    return (
        <motion.article
            ref={ref}
            data-testid={`product-card-${i}`}
            initial={{ opacity: 0, y: 56 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: i * 0.12 }}
            className="group relative rounded-3xl overflow-hidden glass-dark flex flex-col"
        >
            <div className="relative h-52 md:h-60 overflow-hidden">
                <img
                    src={p.img}
                    alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                    loading="lazy"
                />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(8,27,46,0.15) 0%, rgba(8,27,46,0.9) 100%)",
                    }}
                />
                <div className="absolute top-5 left-5 flex items-center gap-2">
                    <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold tracking-wide ${
                            p.accent === "energy"
                                ? "bg-energy text-ocean"
                                : "bg-teal text-white"
                        }`}
                    >
                        <StatusIcon size={13} />
                        {p.status}
                    </span>
                    <span className="px-3 py-1.5 rounded-full text-xs font-bold tracking-wide bg-white/10 text-white backdrop-blur-md border border-white/15">
                        {p.trl}
                    </span>
                </div>
                <span className="absolute bottom-4 right-5 font-display font-black text-white/15 text-6xl leading-none">
                    {p.tag}
                </span>
            </div>

            <div className="p-7 md:p-8 flex flex-col flex-1">
                <div
                    className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                        p.accent === "energy"
                            ? "bg-energy/15 text-energy"
                            : "bg-teal/20 text-teal-light"
                    }`}
                >
                    <p.icon size={20} />
                </div>
                <h3 className="mt-5 font-display font-black text-white text-xl md:text-2xl tracking-tight leading-tight">
                    {p.name}
                </h3>
                <p className="mt-3 text-white/60 text-sm leading-relaxed">
                    {p.blurb}
                </p>
                <ul className="mt-5 space-y-2.5">
                    {p.points.map((pt, k) => (
                        <li
                            key={k}
                            className="flex items-start gap-3 text-white/75 text-sm"
                        >
                            <Leaf
                                size={15}
                                className={`mt-0.5 shrink-0 ${
                                    p.accent === "energy"
                                        ? "text-energy"
                                        : "text-teal-light"
                                }`}
                            />
                            {pt}
                        </li>
                    ))}
                </ul>

                <div className="mt-auto pt-7">
                    {p.cta.to ? (
                        <Link
                            to={p.cta.to}
                            data-testid={`product-cta-${i}`}
                            className="group/btn inline-flex items-center gap-2 text-sm font-bold text-energy hover:gap-3 transition-all"
                        >
                            {p.cta.label}
                            <ArrowUpRight size={16} />
                        </Link>
                    ) : (
                        <button
                            onClick={() => smoothScrollTo(p.cta.section)}
                            data-testid={`product-cta-${i}`}
                            className="group/btn inline-flex items-center gap-2 text-sm font-bold text-teal-light hover:gap-3 transition-all"
                        >
                            {p.cta.label}
                            <ArrowUpRight size={16} />
                        </button>
                    )}
                </div>
            </div>
        </motion.article>
    );
};

export const Products = () => (
    <section
        id="products"
        data-testid="products-section"
        className="relative py-24 md:py-32 px-6 md:px-10 bg-ocean-deep grain overflow-hidden"
    >
        <div
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[70vw] h-[70vw] rounded-full opacity-25 pointer-events-none"
            style={{
                background:
                    "radial-gradient(circle, #138A72 0%, rgba(19,138,114,0) 60%)",
                filter: "blur(120px)",
            }}
        />
        <div className="relative max-w-7xl mx-auto">
            <Reveal>
                <Chapter label="Product Portfolio" dark />
            </Reveal>
            <Reveal delay={0.1}>
                <h2 className="mt-8 font-display font-black text-white text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.04] max-w-3xl">
                    One thesis. Two gradients.
                </h2>
            </Reveal>

            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                {products.map((p, i) => (
                    <ProductCard key={p.tag} p={p} i={i} />
                ))}
            </div>
        </div>
    </section>
);

// ---------------- ENERGY COMPARISON (the sign flip) ----------------
const bars = [
    { id: "swro", label: "Seawater RO", value: 4, generates: false, note: "+3.5 to 4 Wh/L" },
    { id: "ed-cdi", label: "Electrodialysis / CDI", value: 2, generates: false, note: "+1 to 3 Wh/L" },
    { id: "cpsd", label: "CPSD (bench scale)", value: 9.7, generates: true, note: "−6 to −9.7 Wh/L" },
];
const MAX = 10;

const Bar = ({ b, i }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.3 });
    const halfPct = (b.value / MAX) * 50;
    return (
        <div ref={ref} data-testid={`energy-bar-${i}`} className="relative">
            <div className="flex items-baseline justify-between mb-2">
                <span className="text-white font-semibold text-sm md:text-base">
                    {b.label}
                </span>
                <span
                    className={`font-display font-black text-sm md:text-base ${
                        b.generates ? "text-energy" : "text-red-400"
                    }`}
                >
                    {b.note}
                </span>
            </div>
            <div className="relative h-3.5 rounded-full bg-white/8 overflow-hidden">
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/30 -translate-x-1/2 z-10" />
                {b.generates ? (
                    <motion.div
                        className="absolute top-0 bottom-0 rounded-full bg-energy"
                        style={{ right: "50%" }}
                        initial={{ width: 0 }}
                        animate={inView ? { width: `${halfPct}%` } : {}}
                        transition={{
                            duration: 1.1,
                            ease: [0.16, 1, 0.3, 1],
                            delay: 0.15 + i * 0.15,
                        }}
                    />
                ) : (
                    <motion.div
                        className="absolute top-0 bottom-0 rounded-full bg-red-500"
                        style={{ left: "50%" }}
                        initial={{ width: 0 }}
                        animate={inView ? { width: `${halfPct}%` } : {}}
                        transition={{
                            duration: 1.1,
                            ease: [0.16, 1, 0.3, 1],
                            delay: 0.15 + i * 0.15,
                        }}
                    />
                )}
            </div>
        </div>
    );
};

const perfStats = [
    {
        id: "net-energy",
        v: ">0 Wh/L",
        l: "Net energy generated",
        d: "A first for desalination: the system produces energy rather than consuming it.",
    },
    {
        id: "water-quality",
        v: "<500 ppm",
        l: "Water quality output",
        d: "Drinking-grade freshwater meeting global potable standards.",
    },
    {
        id: "feedstock-cost",
        v: "$0",
        l: "Feedstock cost",
        d: "Runs on free industrial acid/alkali waste streams, turning a liability into fuel.",
    },
];

export const EnergyComparison = () => (
    <section
        id="performance"
        data-testid="energy-comparison-section"
        className="relative py-24 md:py-32 px-6 md:px-10 bg-ocean-deep grain overflow-hidden"
    >
        <div
            className="absolute -top-1/3 -right-1/4 w-[60vw] h-[60vw] rounded-full opacity-25 pointer-events-none"
            style={{
                background:
                    "radial-gradient(circle, #A3E635 0%, rgba(163,230,53,0) 60%)",
                filter: "blur(110px)",
            }}
        />
        <div className="relative max-w-7xl mx-auto">
            <div className="max-w-3xl">
                <Reveal>
                    <Chapter label="Performance" dark />
                </Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-6 font-display font-black text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.04]">
                        A third-generation leap: the only system that{" "}
                        <span className="text-energy italic">generates</span>{" "}
                        energy while producing freshwater.
                    </h2>
                </Reveal>
            </div>

            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
                {perfStats.map((s, i) => (
                    <Reveal key={s.id} delay={0.1 + i * 0.08} y={30}>
                        <div
                            className="glass-dark rounded-2xl p-7 h-full glass-card-hover"
                            data-testid={`performance-stat-${i}`}
                        >
                            <div className="font-display font-black text-energy text-4xl md:text-5xl tracking-tighter leading-none">
                                {s.v}
                            </div>
                            <div className="mt-4 text-white font-semibold text-base">
                                {s.l}
                            </div>
                            <p className="mt-2 text-white/60 text-sm leading-relaxed">
                                {s.d}
                            </p>
                        </div>
                    </Reveal>
                ))}
            </div>

            <Reveal delay={0.15} y={40} className="mt-6">
                <div className="rounded-3xl bg-ocean border border-white/10 p-8 md:p-10">
                    <div className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-8">
                        Energy per litre of fresh water
                    </div>
                    <div className="space-y-7">
                        {bars.map((b, i) => (
                            <Bar key={b.id} b={b} i={i} />
                        ))}
                    </div>
                    <div className="mt-5 flex justify-between text-[10px] uppercase tracking-widest font-semibold">
                        <span className="text-energy">
                            &larr; Generates energy
                        </span>
                        <span className="text-red-400">
                            Consumes energy &rarr;
                        </span>
                    </div>
                    <div className="mt-8 pt-6 border-t border-white/10 flex items-center gap-6 text-xs">
                        <span className="flex items-center gap-2 text-red-400">
                            <span className="w-3 h-3 rounded-full bg-red-500" />
                            Consumed
                        </span>
                        <span className="flex items-center gap-2 text-energy">
                            <span className="w-3 h-3 rounded-full bg-energy" />
                            Generated
                        </span>
                    </div>
                </div>
            </Reveal>
        </div>
    </section>
);
